import { useMemo } from 'react'
import {
  NativeSelect,
  Text,
  Box,
  HStack,
  Spinner,
  VStack,
  Button,
  Badge,
  Field,
} from '@chakra-ui/react';
import { FiGitBranch, FiArrowRight } from 'react-icons/fi'
import type {
  QuestionPickerDto,
  ScriptElementFormState,
  ScriptLane,
} from '../../types/script'
import { useQuestionAnswers } from '../../hooks/useScript'

interface SurveyQuestionElementFieldsProps {
  surveyQuestionId: string | null | undefined
  questionOptions: QuestionPickerDto[]
  currentClientId: string
  onChange: (updates: Partial<ScriptElementFormState>) => void
  onCreateBranch?: (branchType: string, branchKey: string, label: string) => void
  lanes?: ScriptLane[]
}

export function SurveyQuestionElementFields({
  surveyQuestionId,
  questionOptions,
  currentClientId,
  onChange,
  onCreateBranch,
  lanes,
}: SurveyQuestionElementFieldsProps) {
  const grouped = useMemo(() => {
    const groups = new Map<string, QuestionPickerDto[]>()
    for (const q of questionOptions) {
      const group = q.surveyGroupLabel || 'Ungrouped'
      if (!groups.has(group)) groups.set(group, [])
      groups.get(group)!.push(q)
    }
    return groups
  }, [questionOptions])

  const selectedQuestion = questionOptions.find(
    (q) => q.id === surveyQuestionId
  )

  const { data: questionAnswers, isLoading: answersLoading } =
    useQuestionAnswers(surveyQuestionId)

  const handleQuestionChange = (questionId: string | null) => {
    onChange({ surveyQuestionId: questionId, answerBranches: {} })
  }

  const getLaneForAnswer = (answerId: string): ScriptLane | undefined => {
    if (!lanes) return undefined
    return lanes.find(
      (l) =>
        l.sourceElementClientId === currentClientId &&
        l.sourceBranchType === 'answer' &&
        l.sourceBranchKey === answerId
    )
  }

  return (
    <>
      <Field.Root>
        <Field.Label fontSize="sm">Survey Question</Field.Label>
        <NativeSelect.Root size="sm">
          <NativeSelect.Field
            value={surveyQuestionId ?? ''}
            onChange={(e) => handleQuestionChange(e.target.value || null)}
            placeholder="Select a survey question...">
            {[...grouped.entries()].map(([groupLabel, questions]) => (
              <optgroup key={groupLabel} label={groupLabel}>
                {questions.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </Field.Root>
      {selectedQuestion && (
        <Text fontSize="sm" color="gray.500" mt={1}>
          {selectedQuestion.question}
        </Text>
      )}
      {questionOptions.length === 0 && (
        <Text fontSize="sm" color="gray.400" fontStyle="italic">
          No survey questions available. Create questions in the Surveys section first.
        </Text>
      )}

      {/* Answer Branching Section */}
      {surveyQuestionId && (
        <Box bg="green.50" p={3} borderRadius="md" mt={2}>
          <Text fontSize="xs" fontWeight="semibold" color="green.600" mb={2}>
            Answer Branching (optional)
          </Text>
          <Text fontSize="xs" color="gray.500" mb={2}>
            Create a separate path for specific answers, or route to an existing step.
          </Text>

          {answersLoading && (
            <HStack py={2}>
              <Spinner size="xs" color="green.400" />
              <Text fontSize="xs" color="gray.500">Loading answers...</Text>
            </HStack>
          )}

          {!answersLoading && questionAnswers && questionAnswers.answers.length > 0 && (
            <VStack gap={2} align="stretch">
              {questionAnswers.answers.map((answer) => {
                const existingLane = getLaneForAnswer(answer.id)

                return (
                  <Box
                    key={answer.id}
                    p={2}
                    bg="surface.2"
                    borderRadius="sm"
                    borderWidth="1px"
                    borderColor="green.100"
                  >
                    <HStack gap={2} justify="space-between">
                      <Text fontSize="sm" fontWeight="medium" flex="1" lineClamp={1}>
                        {answer.answerText}
                      </Text>
                      {existingLane ? (
                        <Badge colorPalette={existingLane.colorScheme} fontSize="2xs" flexShrink={0}>
                          <HStack gap={1}>
                            <FiArrowRight size={10} />
                            <Text>{existingLane.label}</Text>
                          </HStack>
                        </Badge>
                      ) : onCreateBranch ? (
                        <Button
                          size="xs"
                          variant="outline"
                          colorPalette="green"
                          onClick={() =>
                            onCreateBranch('answer', answer.id, answer.answerText)
                          }
                          flexShrink={0}><FiGitBranch />Create Path
                                                  </Button>
                      ) : (
                        <Text fontSize="xs" color="gray.500" fontStyle="italic" flexShrink={0}>
                          Continue to next
                        </Text>
                      )}
                    </HStack>
                  </Box>
                );
              })}
            </VStack>
          )}

          {!answersLoading && questionAnswers && questionAnswers.answers.length === 0 && (
            <Text fontSize="xs" color="gray.400" fontStyle="italic">
              This question has no answers configured.
            </Text>
          )}
        </Box>
      )}
    </>
  );
}
